import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { chunk } from 'lodash/array';
import Overlay from 'react-overlays/Overlay';
import BackgroundCells from './BackgroundCells';
import dates from './utils/dates';
import localizer from './localizer';

import { navigate } from './utils/constants';
import { notify } from './utils/helpers';

import EventRow from './EventRow';
import EventEndingRow from './EventEndingRow';
import Popup from './Popup';

import { dateFormat } from './utils/propTypes';
import {
  segStyle, inRange, eventSegments,
  endOfRange, eventLevels, sortEvents
} from './utils/eventLevels';

const eventsForWeek = (evts, start, end, props) =>
  evts.filter(e => inRange(e, start, end, props));

const isSegmentInSlot = (seg, slot) => seg.left <= slot && seg.right >= slot;

const propTypes = {
  ...EventRow.propTypes,

  culture: PropTypes.string,
  date: PropTypes.instanceOf(Date),
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  dateFormat,
  weekdayFormat: dateFormat,
  popup: PropTypes.bool,
  popupOffset: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
    }),
  ]),
  onSelectEvent: PropTypes.func,
  onSelectSlot: PropTypes.func,
  events: PropTypes.array,
  selectable: PropTypes.bool,
  components: PropTypes.object,
  onNavigate: PropTypes.func,
  onShowMore: PropTypes.func,
  className: PropTypes.string,
};

const MonthView = (props) => {
  const {
    date,
    culture,
    weekdayFormat,
    className,
    events,
    selectable,
    components,
    popup,
    onNavigate,
    onSelectEvent,
    onSelectSlot,
    onShowMore
  } = props;

  const [state, setState] = useState({
    rowLimit: 5,
    needLimitMeasure: true,
    overlay: null
  });

  const firstRowRef = useRef(null);
  const firstDateRowRef = useRef(null);
  const measureEventRef = useRef(null);
  const bgRowsRef = useRef([]);
  const selectTimerRef = useRef(null);
  const pendingSelectionRef = useRef([]);
  const resizeListenerRef = useRef(null);

  const month = dates.visibleDays(date, culture);
  const weeks = chunk(month, 7);
  const weekCount = weeks.length;

  useEffect(() => {
    if (state.needLimitMeasure) {
      measureRowLimit();
    }

    const handleResize = () => {
      requestAnimationFrame(() => {
        setState(prev => ({ ...prev, needLimitMeasure: true }));
      });
    };

    window.addEventListener('resize', handleResize);
    resizeListenerRef.current = handleResize;

    return () => {
      window.removeEventListener('resize', resizeListenerRef.current);
      clearTimeout(selectTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (state.needLimitMeasure) {
      measureRowLimit();
    }
  }, [state.needLimitMeasure]);

  const measureRowLimit = () => {
    if (!measureEventRef.current || !firstDateRowRef.current || !firstRowRef.current) return;

    const eventHeight = measureEventRef.current.offsetHeight;
    const labelHeight = firstDateRowRef.current.offsetHeight;
    const eventSpace = firstRowRef.current.offsetHeight - labelHeight;

    setState(prev => ({
      ...prev,
      needLimitMeasure: false,
      rowLimit: Math.max(Math.floor(eventSpace / eventHeight), 1),
    }));
  };

  const renderMeasureRows = useCallback((levels, row, idx) => {
    const first = idx === 0;
    return first ? (
      <div className="rbc-row">
        <div className="rbc-row-segment" style={segStyle(1, 7)}>
          <div ref={measureEventRef} className={cn('rbc-event')}>
            <div className="rbc-event-content">&nbsp;</div>
          </div>
        </div>
      </div>
    ) : <span />;
  }, []);

  const renderWeek = useCallback((week, weekIdx, content) => {
    const { first, last } = endOfRange(week);
    let weekEvents = eventsForWeek(events, week[0], week[week.length - 1], props);
    
    weekEvents.sort((a, b) => sortEvents(a, b, props));
    const segments = weekEvents.map(evt => eventSegments(evt, first, last, props));
    const limit = (state.rowLimit - 1) || 1;
    const { levels, extra } = eventLevels(segments, limit);

    content = content || ((lvls, wk, idx) => lvls.map((lvl, idx) => renderRowLevel(lvl, wk, idx)));

    return (
      <div
        key={`week_${weekIdx}`}
        className="rbc-month-row"
        ref={weekIdx === 0 ? firstRowRef : null}
      >
        {renderBackground(week, weekIdx)}
        <div className="rbc-row-content">
          <div
            className="rbc-row"
            ref={weekIdx === 0 ? firstDateRowRef : null}
          >
            {renderDates(week)}
          </div>
          {content(levels, week, weekIdx)}
          {!!extra.length &&
            renderShowMore(segments, extra, week, weekIdx, levels.length)
          }
        </div>
        {popup && renderOverlay()}
      </div>
    );
  }, [state.rowLimit, events, popup]);

  const renderBackground = useCallback((row, idx) => {
    const onSelectSlot = ({ start, end }) => {
      pendingSelectionRef.current = pendingSelectionRef.current.concat(row.slice(start, end + 1));
      clearTimeout(selectTimerRef.current);
      selectTimerRef.current = setTimeout(selectDates, 10);
    };

    return (
      <BackgroundCells
        container={() => document.body}
        selectable={selectable}
        slots={7}
        ref={(r) => { bgRowsRef.current[idx] = r; }}
        onSelectSlot={onSelectSlot}
      />
    );
  }, [selectable]);

  const renderRowLevel = useCallback((segments, week, idx) => {
    const { first, last } = endOfRange(week);
    return (
      <EventRow
        {...props}
        eventComponent={components?.event}
        onSelect={(args) => selectEvent(args)}
        key={idx}
        segments={segments}
        start={first}
        end={last}
      />
    );
  }, [components, props]);

  const renderShowMore = useCallback((segments, extraSegments, week, weekIdx) => {
    const { first, last } = endOfRange(week);
    const onClick = (slot) => showMore(segments, week[slot - 1], weekIdx, slot);

    return (
      <EventEndingRow
        {...props}
        eventComponent={components?.event}
        onSelect={(args) => selectEvent(args)}
        onShowMore={onClick}
        key={`last_row_${weekIdx}`}
        segments={extraSegments}
        start={first}
        end={last}
      />
    );
  }, [components, props]);

  const renderDates = useCallback((row) => {
    return row.map((day, colIdx) => {
      const offRange = dates.month(day) !== dates.month(date);
      return (
        <div
          key={`header_${colIdx}`}
          style={segStyle(1, 7)}
          className={cn('rbc-date-cell', {
            'rbc-off-range': offRange,
            'rbc-now': dates.eq(day, new Date(), 'day'),
            'rbc-current': dates.eq(day, date, 'day'),
          })}
        >
          <a href="#" onClick={dateClick(day)}>
            {localizer.format(day, props.dateFormat, culture)}
          </a>
        </div>
      );
    });
  }, [date, culture, props.dateFormat]);

  const renderHeaders = useCallback((row, format, culture) => {
    const first = row[0];
    const last = row[row.length - 1];
    return dates.range(first, last, 'day').map((day, idx) => (
      <div
        key={`header_${idx}`}
        className="rbc-header"
        style={segStyle(1, 7)}
      >
        {localizer.format(day, format, culture)}
      </div>
    ));
  }, []);

  const renderOverlay = useCallback(() => {
    const overlay = state.overlay || {};
    return (
      <Overlay
        rootClose
        placement="bottom"
        container={document.body}
        show={!!overlay.position}
        onHide={() => setState(prev => ({ ...prev, overlay: null }))}
      >
        <Popup
          {...props}
          eventComponent={components?.event}
          position={overlay.position}
          events={overlay.events}
          slotStart={overlay.date}
          slotEnd={overlay.end}
          onSelect={(args) => selectEvent(args)}
        />
      </Overlay>
    );
  }, [state.overlay, components, props]);

  const dateClick = useCallback((date) => (e) => {
    e.preventDefault();
    clearSelection();
    notify(onNavigate, [navigate.DATE, date]);
  }, [onNavigate]);

  const selectEvent = useCallback((...args) => {
    clearSelection();
    notify(onSelectEvent, args);
  }, [onSelectEvent]);

  const selectDates = useCallback(() => {
    const slots = pendingSelectionRef.current.slice();
    pendingSelectionRef.current = [];
    slots.sort((a, b) => +a - +b);
    
    notify(onSelectSlot, {
      slots,
      start: slots[0],
      end: slots[slots.length - 1],
    });
  }, [onSelectSlot]);

  const showMore = useCallback((segments, date, weekIdx, slot) => {
    if (!bgRowsRef.current[weekIdx]?.children?.[slot - 1]) return;

    const cell = bgRowsRef.current[weekIdx].children[slot - 1];
    const events = segments
      .filter(seg => isSegmentInSlot(seg, slot))
      .map(seg => seg.event);

    clearSelection();

    if (popup) {
      const rect = cell.getBoundingClientRect();
      const position = {
        left: rect.left,
        top: rect.top + rect.height,
        width: rect.width,
        height: rect.height
      };

      setState(prev => ({
        ...prev,
        overlay: { date, events, position },
      }));
    } else {
      notify(onNavigate, [navigate.DATE, date]);
    }

    notify(onShowMore, [events, date, slot]);
  }, [popup, onNavigate, onShowMore]);

  const clearSelection = useCallback(() => {
    clearTimeout(selectTimerRef.current);
    pendingSelectionRef.current = [];
  }, []);

  return (
    <div className={cn('rbc-month-view', className)}>
      <div className="rbc-row rbc-month-header">
        {renderHeaders(weeks[0], weekdayFormat, culture)}
      </div>
      {weeks.map((week, idx) =>
        renderWeek(week, idx, state.needLimitMeasure && renderMeasureRows)
      )}
    </div>
  );
};

MonthView.displayName = 'MonthView';
MonthView.propTypes = propTypes;

MonthView.navigate = (date, action) => {
  switch (action) {
    case navigate.PREVIOUS:
      return dates.add(date, -1, 'month');
    case navigate.NEXT:
      return dates.add(date, 1, 'month');
    default:
      return date;
  }
};

MonthView.range = (date, { culture }) => {
  const start = dates.firstVisibleDay(date, culture);
  const end = dates.lastVisibleDay(date, culture);
  return { start, end };
};

export default MonthView;